import type { AgentSuggestedStep } from '@cdf/api-client-react';
import { ArrowRightIcon, MessageSquareIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface AgentSuggestedStepsProps {
  steps: AgentSuggestedStep[];
  /** Disables every step, e.g. while the Agent is busy. */
  disabled: boolean;
  /** Ask steps continue the conversation, so only the latest reply keeps them active. */
  askDisabled: boolean;
  onAsk: (prompt: string) => void;
  onOpen: (destination: string) => void;
}

/** Clickable next steps after an Agent answer. Opening or asking never executes work by itself. */
export function AgentSuggestedSteps({ steps, disabled, askDisabled, onAsk, onOpen }: AgentSuggestedStepsProps) {
  if (steps.length === 0) return null;
  return (
    <section className="mt-3 space-y-2 rounded-md border bg-background p-3 text-xs" aria-label="Suggested next steps">
      <p className="font-medium">Suggested next steps</p>
      <ul className="space-y-2">
        {steps.map(step => {
          const isOpen = step.kind === 'open' && Boolean(step.destination);
          const Icon = isOpen ? ArrowRightIcon : MessageSquareIcon;
          return (
            <li key={step.id} className="flex flex-wrap items-center gap-2">
              <Button type="button" variant="outline" size="sm"
                disabled={disabled || (!isOpen && askDisabled)}
                onClick={() => {
                  if (isOpen && step.destination) onOpen(step.destination);
                  else if (step.prompt) onAsk(step.prompt);
                }}>
                <Icon className="size-3.5" aria-hidden="true" />
                {step.label}
              </Button>
              {step.description && <span className="text-muted-foreground">{step.description}</span>}
            </li>
          );
        })}
      </ul>
      <p className="text-muted-foreground">Opening a page or asking a follow-up does not change anything. Any change still shows a review before it is saved.</p>
    </section>
  );
}
